import type { ReactNode } from 'react';
import type { GuardMode } from '@nadoutong/contracts';
import { useT, type I18nKey } from '../../../i18n/index.ts';
import { cx, Input } from '../../../ui/index.ts';
import type { GuardIssue } from './guardModel.ts';

const MODE_TONE: Readonly<Record<GuardMode, string>> = {
  off: 'bg-active text-paper',
  shadow: 'bg-warn-dim text-warn',
  act: 'bg-ok-dim text-ok',
};

export interface SegmentOption<V extends string> {
  readonly value: V;
  readonly label: string;
  readonly tone?: string;
}

/** Inline segmented choice (DEMO `.seg`); one pressed button per group. */
export function Segment<V extends string>({
  value,
  options,
  disabled,
  ariaLabel,
  testId,
  onChange,
}: {
  readonly value: V;
  readonly options: readonly SegmentOption<V>[];
  readonly disabled: boolean;
  readonly ariaLabel: string;
  readonly testId: string;
  readonly onChange: (value: V) => void;
}): JSX.Element {
  return (
    <span
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      data-value={value}
      className="inline-flex overflow-hidden rounded border border-line bg-well"
    >
      {options.map((option, index) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            data-testid={`${testId}-${option.value}`}
            className={cx(
              'px-2.5 py-0.5 text-label transition-colors duration-120 disabled:cursor-not-allowed disabled:opacity-60',
              index > 0 && 'border-l border-line',
              on ? (option.tone ?? 'bg-active text-paper') : 'text-paper-low hover:text-paper-mid',
            )}
            onClick={() => { if (!on) onChange(option.value); }}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  );
}

export function ModeSegment({
  value,
  disabled,
  ariaLabel,
  testId,
  onChange,
}: {
  readonly value: GuardMode;
  readonly disabled: boolean;
  readonly ariaLabel: string;
  readonly testId: string;
  readonly onChange: (value: GuardMode) => void;
}): JSX.Element {
  const t = useT();
  return (
    <Segment
      value={value}
      disabled={disabled}
      ariaLabel={ariaLabel}
      testId={testId}
      onChange={onChange}
      options={[
        { value: 'off', label: t('guaranteed.guard.mode.off'), tone: MODE_TONE.off },
        { value: 'shadow', label: t('guaranteed.guard.mode.shadow'), tone: MODE_TONE.shadow },
        { value: 'act', label: t('guaranteed.guard.mode.act'), tone: MODE_TONE.act },
      ]}
    />
  );
}

export function NumberField({
  label,
  unit,
  value,
  issue,
  disabled,
  testId,
  placeholder,
  onChange,
  onEnter,
}: {
  readonly label: string;
  readonly unit: I18nKey | null;
  readonly value: string;
  readonly issue: GuardIssue | undefined;
  readonly disabled: boolean;
  readonly testId: string;
  readonly placeholder?: string;
  readonly onChange: (text: string) => void;
  readonly onEnter?: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-0.5 border-t border-dashed border-line-soft py-1 first:border-t-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-label text-paper-mid">{label}</span>
        <span className="flex items-center gap-1.5">
          <Input
            compact
            mono
            wrapperClassName="w-20"
            className="text-right"
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            inputMode="decimal"
            invalid={issue !== undefined}
            aria-label={label}
            data-testid={testId}
            onChange={(event) => onChange(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onEnter?.();
              }
            }}
          />
          <span className="w-6 text-micro text-paper-low">{unit === null ? '' : t(unit)}</span>
        </span>
      </div>
      {issue === undefined ? null : (
        <span role="alert" className="text-right text-micro text-bad" data-testid={`${testId}-error`}>
          {t(issue.key, issue.params)}
        </span>
      )}
    </div>
  );
}

export function CellCap({ children }: { readonly children: ReactNode }): JSX.Element {
  return <div className="mb-1.5 flex items-center justify-between gap-2 text-label text-paper-low">{children}</div>;
}
